import { useEffect, useRef, useState } from 'react';
import { loadClientAiConversation, type ClientAiChatMessage } from '@/lib/coreClientChat';
import AiRichContent from '@/components/feature/AiRichContent';

interface Props {
  clientId: number;
}

/**
 * Affiche, côté marchand, le fil de discussion entre un client et l'assistant
 * IA de la plateforme (lecture seule). Permet de comprendre ce que le client
 * a demandé avant d'écrire ses messages privés.
 */
export default function AiConversationView({ clientId }: Props) {
  const [messages, setMessages] = useState<ClientAiChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadClientAiConversation(clientId)
      .then((conv) => {
        if (!cancelled) setMessages(conv.messages);
      })
      .catch(() => {
        if (!cancelled) setMessages([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [clientId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 min-h-[220px] gap-2 text-foreground-400">
        <i className="ri-loader-4-line animate-spin text-2xl text-accent-500"></i>
        <span className="text-xs">Chargement de la conversation IA…</span>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 min-h-[220px] text-center px-6 text-foreground-400">
        <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center mb-2">
          <i className="ri-robot-2-line text-xl text-accent-600"></i>
        </div>
        <p className="text-sm font-medium text-foreground-700">Aucune conversation IA</p>
        <p className="text-xs mt-1">Ce client n&apos;a pas encore échangé avec l&apos;assistant.</p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-accent-50 border border-accent-100 mb-1">
        <i className="ri-information-line text-accent-600 text-sm"></i>
        <span className="text-[11px] text-accent-900/80">
          Historique de la conversation du client avec l&apos;assistant IA (lecture seule).
        </span>
      </div>

      {messages.map((m, i) => {
        const mine = m.role === 'assistant';
        return (
          <div key={i} className={`flex ${mine ? 'justify-start' : 'justify-end'}`}>
            <div className={`max-w-[82%] px-3.5 py-2 rounded-lg text-sm ${
              mine ? 'bg-background-100 text-foreground-800' : 'bg-accent-100/70 text-foreground-800'
            }`}>
              <div className="flex items-center gap-1.5 mb-0.5">
                <i className={`${mine ? 'ri-robot-2-line text-accent-600' : 'ri-user-3-line text-foreground-500'} text-xs`}></i>
                <span className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400">
                  {mine ? 'Assistant IA' : 'Client'}
                </span>
              </div>
              <AiRichContent content={m.content} />
            </div>
          </div>
        );
      })}
      <div ref={endRef} />
    </div>
  );
}